import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getDailyMenuTemplateComponent } from "./dailyMenuTemplates";
import { getDailyMenuScreenSettingsByCode } from "app/services/api/dailyMenu";
import RotatedIframeScreen from "./RotatedIframeScreen";
import { hardReloadScreen } from "./helpers/hardReloadScreen";

const PAGE_REFRESH_INTERVAL_MS = 60 * 60 * 1000; // 60 minutes

/**
 * Renders Daily Menu by orientation (z URL) a šablónou.
 *
 * ŠABLÓNA sa berie PRIMÁRNE z nastavení obrazovky (settings.template, číta sa naživo cez
 * by-code); URL parameter ?template=... je len FALLBACK (spätná kompatibilita so staršími
 * URL na zariadeniach). Vďaka tomu zmena šablóny v AdminUI nastaveniach zmení obrazovku po
 * auto-refreshi / "Obnoviť obrazovky" bez nutnosti fyzicky meniť URL na zariadení – rovnako
 * ako contentRotation nižšie.
 * .../main/portrait  → portrait komponent (šablóny z nastavení)
 * .../main/landscape → landscape komponent (šablóny z nastavení)
 * Ak šablóna v nastaveniach chýba, použije sa ?template z URL, inak predvolená (template_1).
 *
 * Otočenie obsahu (kompenzácia fyzicky zle orientovanej obrazovky bez natívnej
 * rotácie) – platí VÝLUČNE pre portrait; landscape sa nikdy neotáča bez ohľadu
 * na nastavenie (fyzicky zle otočená portrait obrazovka je jediný reálny prípad).
 * settings.contentRotation aj settings.template sa čítajú vždy naživo (nie z URL),
 * takže zmena nastavenia sa prejaví po "Obnoviť obrazovky" / auto-refreshi. URL parameter
 * __rotated=1 (pridáva ho len RotatedIframeScreen pri stavbe src iframe) rotáciu preskočí,
 * aby sa zabránilo nekonečnému vnáraniu iframe do iframe – šablóna sa však aj vnútri iframe
 * číta z nastavení.
 *
 * Refreshes the page every 60 minutes (landscape and portrait). Výber typu menu podľa času
 * (raňajky/obed/večera) prebieha nezávisle každých 30 s v useDailyMenuScreenData, takže dlhší
 * interval reloadu neovplyvní časové prepínanie obsahu.
 */
function DailyMenuScreenRouter() {
  const { customerCode, orientation } = useParams();
  const [searchParams] = useSearchParams();
  const isPortrait = String(orientation || "").toLowerCase() === "portrait";
  const urlTemplateId = searchParams.get("template") || "";
  const env = searchParams.get("env") || "";
  const isNestedForRotation = searchParams.get("__rotated") === "1";
  const rotationEligible = isPortrait && !isNestedForRotation;

  // Nastavenia obrazovky (naživo z by-code): null = ešte načítavam.
  // Slúžia pre šablónu (vždy) aj pre otočenie obsahu (len portrait).
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getDailyMenuScreenSettingsByCode({ customerCode, env })
      .then((res) => {
        if (!cancelled) setSettings(res?.settings || {});
      })
      .catch(() => {
        if (!cancelled) setSettings({});
      });
    return () => {
      cancelled = true;
    };
  }, [customerCode, env]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      hardReloadScreen();
    }, PAGE_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, []);

  // Kým nepoznáme nastavenia, nič nerenderujeme (krátky čierny blik namiesto
  // bliknutia nesprávnej šablóny / nesprávne otočeného obsahu). Vlastný
  // "Loading…"/logo si rieši samotná šablóna po zvolení správnej vetvy.
  if (settings === null) {
    return <div style={{ position: "fixed", inset: 0, background: "#000" }} />;
  }

  // Otočenie: len portrait (a nie už vnorený iframe).
  const rotationRaw = settings?.contentRotation;
  const rotation =
    rotationEligible && (rotationRaw === "cw" || rotationRaw === "ccw")
      ? rotationRaw
      : "none";
  if (rotation !== "none") {
    return <RotatedIframeScreen rotation={rotation} />;
  }

  // Šablóna: nastavenia vyhrávajú, URL parameter je len fallback, potom predvolená.
  const templateId = String(settings?.template || "").trim() || urlTemplateId;
  const TemplateComponent = getDailyMenuTemplateComponent(
    templateId,
    isPortrait ? "portrait" : "landscape",
  );

  return <TemplateComponent />;
}

export default DailyMenuScreenRouter;
